import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const axiosInstance = axios.create({ withCredentials: true });
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const Analytics = () => {
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [responsesData, setResponsesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const id = window.location.pathname.split('/')[2];
      const [surveyRes, analyticsRes, responsesRes] = await Promise.all([
        axiosInstance.get(`${API}/surveys/${id}`),
        axiosInstance.get(`${API}/surveys/${id}/analytics`),
        axiosInstance.get(`${API}/surveys/${id}/responses`)
      ]);
      setSurvey(surveyRes.data);
      setAnalytics(analyticsRes.data);
      setResponsesData(responsesRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    setGeneratingPdf(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const element = document.getElementById('analytics-content');
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `${survey.title.replace(/[^a-z0-9]/gi, '_')}_Analytics_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      await html2pdf().set(opt).from(element).save();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (loading) return <div className="loading-spinner" data-testid="loading-spinner">Loading...</div>;

  return (
    <div className="dashboard-container" data-testid="analytics">
      <Toaster position="top-right" richColors />
      <div className="analytics-container">
        <div className="builder-header">
          <button className="back-btn" onClick={() => navigate('/dashboard')} data-testid="back-button">← Back to Dashboard</button>
          <button className="download-pdf-btn" onClick={downloadPdf} disabled={generatingPdf} data-testid="download-pdf-btn">
            {generatingPdf ? (<><span className="pdf-spinner"></span>Generating PDF...</>) : (
              <><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>Download PDF</>
            )}
          </button>
        </div>

        <div id="analytics-content">
          <div className="pdf-branding-header" data-testid="pdf-branding">
            {survey.branding && (survey.branding.company_name || survey.branding.company_logo_url) ? (
              <div className="pdf-company-branding">
                {survey.branding.company_logo_url && <img src={survey.branding.company_logo_url} alt={survey.branding.company_name || 'Company Logo'} className="pdf-company-logo" />}
                {survey.branding.company_name && <span className="pdf-company-name">{survey.branding.company_name}</span>}
              </div>
            ) : (
              <div className="pdf-cxrecon-branding">
                <svg className="pdf-cxrecon-logo" viewBox="0 0 100 100" width="32" height="32">
                  <rect x="10" y="10" width="80" height="80" fill="none" stroke="#00897b" strokeWidth="4"/>
                  <circle cx="50" cy="50" r="15" fill="#00897b"/>
                  <line x1="50" y1="35" x2="50" y2="10" stroke="#00897b" strokeWidth="3"/>
                  <line x1="50" y1="65" x2="50" y2="90" stroke="#00897b" strokeWidth="3"/>
                  <line x1="35" y1="50" x2="10" y2="50" stroke="#00897b" strokeWidth="3"/>
                  <line x1="65" y1="50" x2="90" y2="50" stroke="#00897b" strokeWidth="3"/>
                </svg>
                <span className="pdf-powered-by">Powered by <strong>CX/recon</strong></span>
              </div>
            )}
          </div>

          <div className="analytics-header">
            <h1 className="dashboard-title" data-testid="analytics-title">{survey.title} - Analytics</h1>
            <p className="pdf-date">Generated on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value" data-testid="total-responses">{analytics.total_responses}</div>
              <div className="stat-label">Total Responses</div>
            </div>
            {responsesData && (
              <div className="stat-card">
                <div className="stat-value" data-testid="hasty-responses">{responsesData.hasty_survey_count}</div>
                <div className="stat-label">Hasty Responses</div>
                <div className="stat-sublabel">({responsesData.total_count > 0 ? Math.round((responsesData.hasty_survey_count / responsesData.total_count) * 100) : 0}% completed too quickly)</div>
              </div>
            )}
          </div>

          {Object.entries(analytics.question_analytics).map(([qId, data]) => (
            <div key={qId} className="question-analytics" data-testid={`question-analytics-${qId}`}>
              <h3>{data.question_text}</h3>
              
              {(data.type === 'nps' || data.type === 'rating') && (
                <>
                  <div className="statistics-grid" data-testid={`statistics-grid-${qId}`}>
                    <div className="stat-item"><span className="stat-label-small">Min</span><span className="stat-value-small" data-testid={`stat-min-${qId}`}>{data.statistics?.min !== null ? data.statistics.min : '—'}</span></div>
                    <div className="stat-item"><span className="stat-label-small">Max</span><span className="stat-value-small" data-testid={`stat-max-${qId}`}>{data.statistics?.max !== null ? data.statistics.max : '—'}</span></div>
                    <div className="stat-item"><span className="stat-label-small">Mean</span><span className="stat-value-small" data-testid={`stat-mean-${qId}`}>{data.statistics?.mean !== null ? data.statistics.mean.toFixed(2) : '—'}</span></div>
                    <div className="stat-item"><span className="stat-label-small">Median</span><span className="stat-value-small" data-testid={`stat-median-${qId}`}>{data.statistics?.median !== null ? Number(data.statistics.median).toFixed(2) : '—'}</span></div>
                    <div className="stat-item"><span className="stat-label-small">Mode</span><span className="stat-value-small" data-testid={`stat-mode-${qId}`}>{data.statistics?.mode !== null ? (Array.isArray(data.statistics.mode) ? data.statistics.mode.join(', ') : data.statistics.mode) : '—'}</span></div>
                  </div>
                  <p className="responses-count">Responses: {data.count}</p>
                  <div>
                    {Object.entries(data.distribution).map(([score, count]) => {
                      const percentage = data.count > 0 ? ((count / data.count) * 100).toFixed(1) : 0;
                      return (
                        <div key={score} className="chart-bar">
                          <div className="chart-label">{score}</div>
                          <div className="chart-bar-fill" style={{ width: `${(count / data.count) * 300}px` }} data-testid={`bar-${score}`}>
                            <span className="bar-value">{count}, {percentage}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {data.type === 'multiple_choice' && (
                <div>
                  {Object.entries(data.distribution).map(([option, count]) => {
                    const percentage = analytics.total_responses > 0 ? ((count / analytics.total_responses) * 100).toFixed(1) : 0;
                    return (
                      <div key={option} className="chart-bar">
                        <div className="chart-label">{option}</div>
                        <div className="chart-bar-fill" style={{ width: `${(count / analytics.total_responses) * 300}px` }} data-testid={`bar-${option}`}>
                          <span className="bar-value">{count}, {percentage}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {data.type === 'text' && (
                <div>
                  <p>Total responses: {data.count}</p>
                  <p><strong>Sample responses:</strong></p>
                  <ul>{data.sample_responses.map((resp, i) => <li key={i} style={{ marginBottom: '0.5rem' }}>{resp}</li>)}</ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
